import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LogoTile from "@/components/app/LogoTile";
import AuthLayout from "@/components/app/AuthLayout";
import PasswordInput from "@/components/app/PasswordInput";

const ResetPasswordPage = () => {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();
    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const clientIssue = useMemo(() => {
        if (!token) return "Missing reset token in the URL.";
        if (pw1 && pw1.length < 8) return "Password must be at least 8 characters.";
        if (pw1 && pw2 && pw1 !== pw2) return "Passwords do not match.";
        return "";
    }, [token, pw1, pw2]);

    if (user && user !== false) return <Navigate to="/reports" replace />;

    const canSubmit = !!token && pw1.length >= 8 && pw1 === pw2 && !busy;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError("");
        setBusy(true);
        try {
            await api.post("/auth/password-reset/confirm", {
                token,
                new_password: pw1,
            });
            nav("/login", {
                replace: true,
                state: {
                    banner:
                        "Password reset — please log in with your new password.",
                },
            });
        } catch (err) {
            const s = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (s === 400 && detail?.error === "INVALID_OR_EXPIRED_TOKEN") {
                setError(
                    "This reset link is invalid or expired. Please request a new one."
                );
            } else {
                setError(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthLayout testId="reset-password-page">
            <div className="min-h-screen w-full flex items-center justify-center px-4 py-10 route-fade">
            <div className="w-full max-w-[440px]">
                <div className="flex flex-col items-center mb-8">
                    <LogoTile size={72} className="mb-5" />
                    <h1 className="text-3xl font-semibold text-slate-900 text-center">
                        Set a new password
                    </h1>
                    <p className="text-[14px] text-slate-600 mt-2 text-center">
                        This link expires 30 minutes after you request it.
                    </p>
                </div>

                <div className="glass-card">
                    {!token ? (
                        <div
                            className="flex flex-col items-start gap-3"
                            data-testid="reset-missing-token"
                        >
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-600">
                                <AlertCircle size={20} strokeWidth={2} />
                            </div>
                            <p className="text-[14px] text-slate-700 leading-relaxed">
                                The reset token is missing from the URL. Request a new reset
                                link.
                            </p>
                            <Link to="/forgot-password" className="btn btn-primary">
                                Request a new link
                            </Link>
                        </div>
                    ) : (
                        <form
                            onSubmit={submit}
                            className="flex flex-col gap-4"
                            data-testid="reset-form"
                        >
                            <div>
                                <label className="label label-required" htmlFor="rp-pw1">
                                    New password
                                </label>
                                <PasswordInput
                                    id="rp-pw1"
                                    autoComplete="new-password"
                                    minLength={8}
                                    required
                                    value={pw1}
                                    onChange={(e) => setPw1(e.target.value)}
                                    data-testid="reset-pw1"
                                />
                                <div className="hint">At least 8 characters.</div>
                            </div>
                            <div>
                                <label className="label label-required" htmlFor="rp-pw2">
                                    Confirm password
                                </label>
                                <PasswordInput
                                    id="rp-pw2"
                                    autoComplete="new-password"
                                    minLength={8}
                                    required
                                    value={pw2}
                                    onChange={(e) => setPw2(e.target.value)}
                                    data-testid="reset-pw2"
                                />
                                {clientIssue && pw1 && pw2 && (
                                    <div className="err" data-testid="reset-client-error">
                                        {clientIssue}
                                    </div>
                                )}
                            </div>
                            {error && (
                                <div
                                    className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2"
                                    data-testid="reset-error"
                                >
                                    {error}
                                    <div className="mt-2">
                                        <Link
                                            to="/forgot-password"
                                            className="text-emerald-700 hover:text-emerald-800"
                                        >
                                            Request a new reset link →
                                        </Link>
                                    </div>
                                </div>
                            )}
                            <button
                                type="submit"
                                className="btn btn-primary w-full py-3 mt-1"
                                disabled={!canSubmit}
                                data-testid="reset-submit-button"
                            >
                                {busy ? "Updating…" : "Update password"}
                            </button>
                            <Link
                                to="/login"
                                className="text-[13px] text-emerald-700 hover:text-emerald-800 self-center mt-1"
                            >
                                <ArrowLeft size={12} className="inline" strokeWidth={2} /> Back
                                to log in
                            </Link>
                        </form>
                    )}
                </div>
            </div>
            </div>
        </AuthLayout>
    );
};

export default ResetPasswordPage;
